import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Car, Clock, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LatLngExpression } from 'leaflet'
import { Card, CardContent } from '@/components/ui/card'
import { useAccounts } from '@/lib/use-accounts'
import { useCountdown } from '@/lib/use-countdown'
import { useFeatureFlag } from '@/lib/use-feature-flags'
import { cn } from '@/lib/utils'
import { localizeDigits } from '@/lib/localize-digits'

interface RentalBannerProps {
  userLocation: LatLngExpression | null
}

function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371 // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function getWalkingMinutes(distanceKm: number): number {
  return Math.round((distanceKm / 5) * 60)
}

export function RentalBanner({ userLocation }: RentalBannerProps) {
  const [enabled] = useFeatureFlag('rentalDashboard')
  const { accounts } = useAccounts()
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Find the first account with an active rental (reserved or ongoing trip)
  const activeAccount = useMemo(
    () =>
      accounts.find(
        (acc) => acc.status === 'reserved' || acc.status === 'ongoing-trip',
      ),
    [accounts],
  )

  const countdown = useCountdown(activeAccount?.reservationEndTime ?? null)

  const remainingMs = activeAccount?.reservationEndTime
    ? activeAccount.reservationEndTime.getTime() - Date.now()
    : null

  // Color-coded based on remaining time
  const urgencyClass = useMemo(() => {
    if (!remainingMs)
      return 'border-green-500/50 bg-green-50 dark:bg-green-950/30'
    const minutes = remainingMs / 60000
    if (minutes < 10) return 'border-red-500/50 bg-red-50 dark:bg-red-950/30'
    if (minutes < 30)
      return 'border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/30'
    return 'border-green-500/50 bg-green-50 dark:bg-green-950/30'
  }, [remainingMs])

  const urgencyTextClass = useMemo(() => {
    if (!remainingMs) return 'text-green-700 dark:text-green-400'
    const minutes = remainingMs / 60000
    if (minutes < 10) return 'text-red-700 dark:text-red-400'
    if (minutes < 30) return 'text-yellow-700 dark:text-yellow-400'
    return 'text-green-700 dark:text-green-400'
  }, [remainingMs])

  // Calculate walking distance
  const walkingInfo = useMemo(() => {
    if (!userLocation || !activeAccount?.vehicleLocation) return null
    const userLat = Array.isArray(userLocation)
      ? (userLocation as [number, number])[0]
      : 0
    const userLng = Array.isArray(userLocation)
      ? (userLocation as [number, number])[1]
      : 0
    const dist = haversineDistance(
      userLat,
      userLng,
      activeAccount.vehicleLocation.latitude,
      activeAccount.vehicleLocation.longitude,
    )
    const minutes = getWalkingMinutes(dist)
    return { distanceKm: dist, minutes }
  }, [userLocation, activeAccount?.vehicleLocation])

  if (!enabled || !activeAccount) return null

  const carName =
    activeAccount.status === 'ongoing-trip'
      ? activeAccount.tripCar
      : activeAccount.reservationCar

  const statusLabel =
    activeAccount.status === 'ongoing-trip'
      ? t('rentalBanner.tripInProgress')
      : t('rentalBanner.reserved')

  return (
    <div className="absolute inset-x-0 top-0 z-[1000] p-3">
      <Card
        className={cn('border cursor-pointer shadow-lg', urgencyClass)}
        onClick={() =>
          navigate({
            to: '/profile/$accountId',
            params: { accountId: activeAccount.id },
          })
        }
      >
        <CardContent className="p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                  activeAccount.status === 'ongoing-trip'
                    ? 'bg-blue-100 dark:bg-blue-900/50'
                    : 'bg-green-100 dark:bg-green-900/50',
                )}
              >
                <Car
                  className={cn(
                    'h-5 w-5',
                    activeAccount.status === 'ongoing-trip'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-green-600 dark:text-green-400',
                  )}
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">
                  {carName ?? t('rentalBanner.vehicle')}
                </p>
                <p className="text-xs text-muted-foreground">{statusLabel}</p>
              </div>
            </div>
            <div className="text-end shrink-0">
              {countdown && (
                <div className="flex items-center gap-1">
                  <Clock className={cn('h-3.5 w-3.5', urgencyTextClass)} />
                  <span
                    className={cn(
                      'text-sm font-mono font-bold',
                      urgencyTextClass,
                    )}
                  >
                    {countdown}
                  </span>
                </div>
              )}
              {walkingInfo && (
                <div className="flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {t('rentalBanner.walkingTime', {
                      minutes: localizeDigits(String(walkingInfo.minutes)),
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
